import PageBannerSlot from '@/components/common/banner/PageBannerSlot';
import TableList from '@/components/common/organism/TableList';
import PageState from '@/components/common/PageState';
import NoticeTable from "@/pages/Notice/components/NoticeTable";
import FaqList from '@/pages/Notice/components/FaqList';
import { ListBody, ListPageShell, ListTabRow, ListTabs, ListTypeTab, useListHeaderHeight } from '@/components/common/organism/ListPageLayout';
import {useNavigate, useSearchParams} from "react-router-dom";
import { useListKey, useUiScope } from '@/common/useUiScope';
import { UI_SCREEN } from '@/common/uiEventNames';

type NoticeTab = 'notice' | 'faq';
const TAB_PARAM = 'tab';

/**
 * 공지사항 — [공지] [자주 묻는 질문] 두 탭. 공통 목록 레이아웃(UI 가이드 6.1): 흰 컨테이너 + 탭 줄(고정) + 필터 줄 + 본문.
 * 자주 묻는 질문은 서버의 명치사 기능 안내서(문의 자동 답변 근거와 같은 원본)에서 온다. 주소 `?tab=faq&faq=<id>` 로 바로 연다.
 */
const Notice = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchQuery = searchParams.get('search') || '';
    const tab: NoticeTab = searchParams.get(TAB_PARAM) === 'faq' ? 'faq' : 'notice';
    const headerHeight: number = useListHeaderHeight(true);
    // 노출·클릭 기록 — 공지 탭만. 검색어가 바뀌면 새 목록 표시(검색어 원문은 해시로만 listKey 에 들어간다)
    const trackListKey: string = useListKey(`notices:${searchQuery}`);
    const listBodyRef = useUiScope({ screen: UI_SCREEN.NOTICE_LIST, section: 'list', listKey: trackListKey });

    // 상단·하단 배너는 공용 자리(PageBannerSlot)가 받는다 — 배너만 받으려고 공지 목록을 한 번 더 부르던 요청은 뺐다(2026-10-12)

    // 상세의 [공지 목록] 버튼이 이 표시를 보고 뒤로가기로 돌아와 보던 목록(쪽·스크롤)을 되살린다
    const handleClick = (noticeId:number) => {
        navigate(`/notices/${noticeId}`, { state: { fromNoticeList: true } });
    }

    const changeTab = (next: NoticeTab) => {
        if (next === tab) return;
        const params = new URLSearchParams();
        if (next === 'faq') params.set(TAB_PARAM, 'faq');
        setSearchParams(params);
        window.scrollTo({ top: 0 });
    };


    return (
        <ListPageShell data-list-page="notice">
            <ListTabRow style={{ top: headerHeight }}>
                <ListTabs role="tablist" aria-label="공지사항">
                    <ListTypeTab type="button" role="tab" active={tab === 'notice'} aria-selected={tab === 'notice'} onClick={() => changeTab('notice')}>공지</ListTypeTab>
                    <ListTypeTab type="button" role="tab" active={tab === 'faq'} aria-selected={tab === 'faq'} onClick={() => changeTab('faq')}>자주 묻는 질문</ListTypeTab>
                </ListTabs>
            </ListTabRow>

            {tab === 'faq' ? (
                <FaqList />
            ) : (
                <ListBody ref={listBodyRef}>
                    <PageBannerSlot placement="top" variant="list" />
                    <TableList
                        loadingPlacement="page"
                        bottomSpacer={false}
                        key={searchQuery}
                        onClick={handleClick}
                        fetchUrl={searchQuery ? `/notices?search=${encodeURIComponent(searchQuery)}` : '/notices'}
                        headers={[]}
                        listCount={15}
                        windowSize={5}
                        InnerTable={NoticeTable}
                        emptyMessage={
                            searchQuery
                                ? <PageState
                                    title="검색 결과가 없습니다"
                                    description={<>'{searchQuery}' 와(과) 일치하는 공지사항을 찾지 못했어요.</>}
                                    action={{ label: '전체 공지 보기', to: '/notices' }}
                                  />
                                : <PageState
                                    title="등록된 공지사항이 없어요"
                                    description="새 소식이 올라오면 이곳에서 가장 먼저 알려 드릴게요."
                                    action={{ label: '홈으로', to: '/' }}
                                  />
                        }
                    />
                    <PageBannerSlot placement="bottom" variant="list" />
                </ListBody>
            )}
        </ListPageShell>
    );
};

export default Notice;
